import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { WalletsResponse } from '@shared/models/wallet.model';
import { environment } from '@/environments/environment';
import { withSuppressErrorToast } from '@core/http/api-response.envelope';
import {
  CreateWalletRequest,
  UpdateWalletRequest,
  WalletMutateResponse,
  RetryWalletKeygenResponse,
  GetAssetsResponse,
  WalletTransactionsQuery,
  WalletTransactionsResponse,
  WalletTransactionItemApiDto,
  WalletTransactionsApiEnvelope,
  WalletTransactionType,
  WalletTransactionItem,
  WalletUnifiedStatus,
  WalletWithdrawalPrevalidateResponse,
  WalletTransactionPartyDto,
} from '../models/wallet.model';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private readonly baseUrl = `${environment.assetManagementApiUrl}/api/v1`;
  private readonly transactionManagementApiUrl = `${environment.transactionManagementApiUrl}/api/v1`;

  constructor(private http: HttpClient) {}

  private toHex(n: number): string {
    return n.toString(16).toUpperCase();
  }

  private padHex(hex: string, length: number): string {
    return hex.padStart(length, '0');
  }

  getWalletTransactions(
    walletId: string,
    query: WalletTransactionsQuery
  ): Observable<WalletTransactionsResponse> {
    return this.http
      .get<
        WalletTransactionsApiEnvelope | WalletTransactionsResponse
      >(`${this.transactionManagementApiUrl}/wallet/${walletId}/transactions`, { params: { ...query } })
      .pipe(
        map(body => this.normalizeWalletTransactionsResponse(body, query.type))
      );
  }

  private partyAddress(
    party: string | WalletTransactionPartyDto | undefined
  ): string {
    if (party == null) return '';
    return typeof party === 'string' ? party : (party.address ?? '');
  }

  private mapApiTransactionStatus(raw: string): WalletUnifiedStatus {
    const u = String(raw).toUpperCase();
    if (u === 'FINALIZED' || u === 'DONE') return 'DONE';
    if (u === 'FAILED' || u === 'REJECTED') return 'REJECTED';
    if (u === 'BROADCASTED') return 'BROADCASTED';
    if (u === 'WAITING_APPROVAL' || u === 'PENDING' || u === 'PROCESSING')
      return 'WAITING_APPROVAL';
    return 'WAITING_APPROVAL';
  }

  private normalizeWalletTransactionItem(
    raw: WalletTransactionItem | WalletTransactionItemApiDto,
    requestTabType: Exclude<WalletTransactionType, 'ALL'>
  ): WalletTransactionItem {
    const r = raw as WalletTransactionItemApiDto;
    const type: Exclude<WalletTransactionType, 'ALL'> =
      r.type ??
      (r.direction === 'IN'
        ? 'DEPOSIT'
        : r.direction === 'OUT'
          ? 'WITHDRAWAL'
          : requestTabType);

    return {
      id: r.id,
      amount: r.amount,
      fee: r.fee,
      hash: r.hash ?? '',
      src: this.partyAddress(r.src),
      dst: this.partyAddress(r.dst),
      status: this.mapApiTransactionStatus(r.status),
      createdBy: r.createdBy ?? null,
      createdAt: r.createdAt ?? '',
      type,
      kind: r.kind ?? 'TRANSFER',
    };
  }

  private normalizeWalletTransactionsResponse(
    body: WalletTransactionsApiEnvelope | WalletTransactionsResponse,
    requestType: WalletTransactionType
  ): WalletTransactionsResponse {
    const dataRaw = body.data ?? [];
    const pagination = body.pagination;
    const tabType: Exclude<WalletTransactionType, 'ALL'> =
      requestType === 'ALL' ? 'DEPOSIT' : requestType;

    return {
      data: dataRaw.map(item =>
        this.normalizeWalletTransactionItem(item, tabType)
      ),
      pagination,
    };
  }

  getWallets(
    page: number = 1,
    limit: number = 10
  ): Observable<WalletsResponse> {
    return this.http.get<WalletsResponse>(`${this.baseUrl}/wallets`, {
      params: { page: page.toString(), limit: limit.toString() },
    });
  }

  /**
   * Get assets list
   * @returns Observable of assets response
   */
  getAssets(): Observable<GetAssetsResponse> {
    // TODO: Replace with actual API call when ready
    return this.http.get<GetAssetsResponse>(`${this.baseUrl}/asset`);
  }

  /**
   * Create a new wallet
   * @param request - The wallet creation request (`timeBasedLimits.allowedDaysOfWeek`: API weekday codes, e.g. 7 Saturday)
   * @returns Observable of create wallet response
   */
  createWallet(request: CreateWalletRequest): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet`,
      request
    );
  }

  /**
   * Update an existing wallet
   * @param walletId - The wallet ID to update
   * @param request - The wallet update request (`settings.timeBasedLimits.allowedDaysOfWeek`)
   * @returns Observable of update wallet response
   */
  updateWallet(
    walletId: string,
    request: UpdateWalletRequest
  ): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/update`,
      request
    );
  }

  /**
   * Disable a wallet
   * @param walletId - The wallet ID to disable
   * @returns Observable of disable wallet response
   */
  disableWallet(walletId: string): Observable<WalletMutateResponse> {
    // TODO: Replace with actual API call when ready
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/disable`,
      {}
    );
  }

  /**
   * Enable a wallet
   * @param walletId - The wallet ID to enable
   * @returns Observable of enable wallet response
   */
  enableWallet(walletId: string): Observable<WalletMutateResponse> {
    // TODO: Replace with actual API call when ready
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/enable`,
      {}
    );
  }

  /**
   * Retry the key generation of a wallet whose key never arrived
   * (vault-management V-7). OWNER only; no approval.
   */
  retryWalletKeygen(walletId: string): Observable<RetryWalletKeygenResponse> {
    return this.http.post<RetryWalletKeygenResponse>(
      `${this.baseUrl}/wallet/${walletId}/keygen/retry`,
      {}
    );
  }

  linkWhitelistAddressToWallet(
    walletId: string,
    whitelistAddressId: string
  ): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/${whitelistAddressId}`,
      {}
    );
  }
  disableWalletWhitelistAddress(
    walletId: string,
    whitelistAddressId: string
  ): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/${whitelistAddressId}/disable`,
      {}
    );
  }

  enableWalletWhitelistAddress(
    walletId: string,
    whitelistAddressId: string
  ): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/${whitelistAddressId}/enable`,
      {}
    );
  }

  /**
   * Unassign (remove) a whitelist address from a wallet.
   * POST /api/v1/wallet/{walletId}/{whitelistAddressId}/unassign
   */
  unassignWhitelistAddressFromWallet(
    walletId: string,
    whitelistAddressId: string
  ): Observable<WalletMutateResponse> {
    return this.http.post<WalletMutateResponse>(
      `${this.baseUrl}/wallet/${walletId}/${whitelistAddressId}/unassign`,
      {}
    );
  }

  prevalidateWithdrawal(
    walletId: string
  ): Observable<WalletWithdrawalPrevalidateResponse> {
    // A refusal opens the reasons modal; WalletEffects toasts other failures.
    return this.http.get<WalletWithdrawalPrevalidateResponse>(
      `${this.baseUrl}/wallet/${walletId}/withdrawal/prevalidate`,
      { context: withSuppressErrorToast() }
    );
  }
}
